import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  cleanupAllProjects,
  hasOwnedSheets,
} from "@/features/projects/excel/sheetManager";

/**
 * A practice (non-exam) run in progress. While it exists the exercise sheets
 * stay in the workbook, so the user can visit Inicio or the translator and
 * come back. Only "Detener práctica" or "Finalizar práctica" clean up.
 */
export interface PracticeSession {
  practicaId: string;
  projectId: string;
  /**
   * Task results graded during this session, per project. Practice is meant
   * to be repeated, so the pane only shows these, never older attempts.
   */
  results: Record<string, Record<string, boolean>>;
}

interface PracticeSessionContextType {
  session: PracticeSession | null;
  /** Starting a project clears its results from this session. */
  startSession: (practicaId: string, projectId: string) => void;
  saveSessionResults: (
    projectId: string,
    results: Record<string, boolean>
  ) => void;
  /** Delete the exercise sheets and end the session. */
  stopSession: () => Promise<void>;
}

const STORAGE_KEY = "excelImparable.practiceSession";

function readStoredSession(): PracticeSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PracticeSession>;
    return parsed.practicaId && parsed.projectId
      ? {
          practicaId: parsed.practicaId,
          projectId: parsed.projectId,
          results: parsed.results ?? {},
        }
      : null;
  } catch {
    return null;
  }
}

function storeSession(session: PracticeSession | null) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked — the session just won't survive a reload.
  }
}

const PracticeSessionContext =
  createContext<PracticeSessionContextType | null>(null);

export function usePracticeSession() {
  const context = useContext(PracticeSessionContext);
  if (!context) {
    throw new Error(
      "usePracticeSession must be used within PracticeSessionProvider"
    );
  }
  return context;
}

export function PracticeSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PracticeSession | null>(
    readStoredSession
  );

  // A remembered session only means something if its sheets are still in
  // this workbook (the pane may have reopened on another file).
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    hasOwnedSheets()
      .then((found) => {
        if (!found && !cancelled) {
          setSession(null);
          storeSession(null);
        }
      })
      .catch(() => {
        // Not in Excel — keep the session as is.
      });
    return () => {
      cancelled = true;
    };
    // Only on mount: later sessions are created right after their sheets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSession = useCallback((practicaId: string, projectId: string) => {
    setSession((prev) => {
      const results =
        prev?.practicaId === practicaId ? { ...prev.results } : {};
      delete results[projectId];
      const next = { practicaId, projectId, results };
      storeSession(next);
      return next;
    });
  }, []);

  const saveSessionResults = useCallback(
    (projectId: string, results: Record<string, boolean>) => {
      setSession((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          results: { ...prev.results, [projectId]: results },
        };
        storeSession(next);
        return next;
      });
    },
    []
  );

  const stopSession = useCallback(async () => {
    await cleanupAllProjects();
    setSession(null);
    storeSession(null);
  }, []);

  return (
    <PracticeSessionContext.Provider
      value={{ session, startSession, saveSessionResults, stopSession }}
    >
      {children}
    </PracticeSessionContext.Provider>
  );
}
